import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
type GridLayout = 'md-3' | 'md-2-lg-3' | 'md-2-lg-4' | 'md-3-lg-5';
interface CarouselProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  label: string;
  /** Slide width while the rail is active. Leave a peek of the next card. */
  itemClassName?: string;
  /** From the given breakpoint up, render as a static grid instead of a rail. */
  grid?: GridLayout;
}
const GRID_BASE = 'md:mx-0 md:grid md:gap-6 md:overflow-visible md:px-0 md:pb-0';
const GRID_CLASSES: Record<GridLayout, string> = {
  'md-3': `${GRID_BASE} md:grid-cols-3`,
  'md-2-lg-3': `${GRID_BASE} md:grid-cols-2 lg:grid-cols-3 lg:gap-8`,
  'md-2-lg-4': `${GRID_BASE} md:grid-cols-2 lg:grid-cols-4`,
  'md-3-lg-5': `${GRID_BASE} md:grid-cols-3 lg:grid-cols-5 lg:gap-5`
};

/**
 * Native scroll-snap rail for phones: swipe, arrow buttons and arrow keys.
 * Turns into a grid from md up when `grid` is set. Contained in its section:
 * must sit inside a `px-5 sm:px-8` container and bleeds only to that edge.
 */
export function Carousel<T>({
  items,
  getKey,
  renderItem,
  label,
  itemClassName = 'w-[78%] sm:w-[46%]',
  grid
}: CarouselProps<T>) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const stepWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.children.length === 0) return 0;
    const first = track.children[0] as HTMLElement;
    const second = track.children[1] as HTMLElement | undefined;
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const step = stepWidth();
      if (!step) return;
      setActive(Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
      setAtStart(track.scrollLeft <= 4);
      setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    track.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    return () => {
      track.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items, stepWidth]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(items.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: clamped * stepWidth(),
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowRight' && !atEnd) {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === 'ArrowLeft' && !atStart) {
      event.preventDefault();
      goTo(active - 1);
    }
  };
  const arrowClass = 'flex h-12 w-12 items-center justify-center rounded-full bg-paper text-graphite ring-1 ring-inset ring-graphite/15 transition-[box-shadow,color] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-35 enabled:hover:text-coral-ink enabled:hover:ring-coral/50 focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/40';
  return <div className="w-full min-w-0 max-w-full" data-id="element-39" data-mp="9q0s2zk1s3ns">
      <ul ref={trackRef} aria-label={label} tabIndex={0} onKeyDown={onKeyDown} className={`no-scrollbar flex w-full min-w-0 max-w-full snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain pb-3 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-coral/30 ${grid ? GRID_CLASSES[grid] : ''}`} data-id="element-40" data-mp="9opph32c91y9">
        {items.map((item, index) => <li key={getKey(item, index)} aria-label={`${index + 1} de ${items.length}`} className={`flex min-w-0 shrink-0 snap-start ${itemClassName} ${grid ? 'md:w-auto' : ''}`} data-id="element-41" data-mp="30rb44palvphu" data-mpi={index}>
            {renderItem(item, index)}
          </li>)}
      </ul>

      <div className={`mt-4 flex items-center justify-between gap-4 ${grid ? 'md:hidden' : ''}`} data-id="element-42" data-mp="3ogvpfeu8eyua">
        <div className="flex min-w-0 items-center gap-3" data-id="element-43" data-mp="3jo2koqyrjqi7">
          <span aria-live="polite" className="whitespace-nowrap text-[0.9rem] font-bold tabular-nums text-graphite" data-id="element-44" data-mp="3eq0z9zdv7f3k">
            {active + 1} / {items.length}
          </span>
          <span className="flex items-center gap-1.5" aria-hidden data-id="element-45" data-mp="g23uvzigfu9q">
            {items.map((item, index) => <span key={getKey(item, index)} className={`block h-[3px] rounded-full transition-[width,background-color] duration-200 ease-out ${index === active ? 'w-6 bg-coral' : 'w-2.5 bg-graphite/20'}`} data-id="element-46" data-mp="b7hgdnh2tllf" data-mpi={index} />)}
          </span>
        </div>
        <div className="flex shrink-0 gap-2" data-id="element-47" data-mp="xi96jgq9niuw">
          <button type="button" onClick={() => goTo(active - 1)} disabled={atStart} aria-label="Anterior" className={arrowClass} data-id="element-48" data-mp="xj11qqfybekn">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-49" data-mp="16ie8hbcoxp1o" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={atEnd} aria-label="Siguiente" className={arrowClass} data-id="element-50" data-mp="16g1kohk94kgp">
            <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-51" data-mp="1g8qul805lq9x" />
          </button>
        </div>
      </div>
    </div>;
}